import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Home: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

        {/* Gradient Orbs */}
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-orange-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-red-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>

        <div className="relative px-4 py-24 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 text-sm font-medium text-primary-400 bg-primary-500/10 border border-primary-500/20 rounded-full backdrop-blur-sm">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              Optimized for Eddystone Beacons
            </div>

            {/* Main Heading */}
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
              <span className="block">Shorten URLs</span>
              <span className="block bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">
                Built for IoT
              </span>
            </h1>

            {/* Subtitle */}
            <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
              Create ultra-short URLs optimized for Eddystone beacons and IoT devices.
              Professional URL shortening with advanced analytics and API integration.
            </p>

            {/* CTA Buttons */}
            {!currentUser ? (
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
                <Link
                  to="/register"
                  className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                >
                  <span className="relative z-10">Get Started Free</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-300 transition-all duration-300 border border-gray-600 rounded-macos-lg hover:text-white hover:border-gray-500 hover:bg-gray-800/50 backdrop-blur-sm"
                >
                  Sign In
                </Link>
              </div>
            ) : (
              <div className="flex justify-center">
                <Link
                  to="/dashboard"
                  className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                >
                  <span className="relative z-10">Go to Dashboard</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 text-center">
              <div>
                <div className="text-3xl font-bold text-white">99.9%</div>
                <div className="text-sm text-gray-400">Uptime</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">16B</div>
                <div className="text-sm text-gray-400">Optimized</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">API</div>
                <div className="text-sm text-gray-400">Ready</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-24 bg-gray-800/50 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
              Built for Modern IoT
            </h2>
            <p className="text-lg text-gray-300">
              Every feature designed with Eddystone beacon constraints and IoT device limitations in mind.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Feature 1 */}
            <div className="group relative p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-primary-500 to-orange-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Eddystone Optimized</h3>
              <p className="text-gray-300 leading-relaxed">
                URLs automatically optimized for Eddystone beacon 16-byte middle section constraints.
                Perfect encoding for maximum compatibility.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group relative p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Powerful API</h3>
              <p className="text-gray-300 leading-relaxed">
                RESTful API with authentication, rate limiting, and comprehensive documentation.
                Integrate seamlessly into your applications.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group relative p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Advanced Analytics</h3>
              <p className="text-gray-300 leading-relaxed">
                Detailed click analytics, geographic data, and device insights.
                Flexible pricing plans to scale with your needs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Contact <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Us</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Get in touch with our team. We're here to help you with your URL shortening needs.
            </p>
          </div>

          {/* Contact Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Office Address */}
            <div className="bg-gray-800/50 backdrop-blur-macos border border-gray-700 rounded-macos-lg p-8 shadow-macos-lg hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">Office Address</h3>
                </div>
              </div>
              <div className="text-gray-300 leading-relaxed">
                <p>Villebois Office Park 2</p>
                <p>920 De Villebois Mareuil Drive</p>
                <p>Moreleta Park, Pretoria</p>
                <p>Gauteng, 0044</p>
                <p>South Africa</p>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-gray-800/50 backdrop-blur-macos border border-gray-700 rounded-macos-lg p-8 shadow-macos-lg hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">Hotline</h3>
                </div>
              </div>
              <div className="text-gray-300">
                <a
                  href="tel:+27861734772"
                  className="text-xl font-medium hover:text-primary-400 transition-colors"
                >
                  +27 861 7347 72
                </a>
                <p className="text-sm text-gray-400 mt-2">
                  Available during business hours
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="bg-gray-800/50 backdrop-blur-macos border border-gray-700 rounded-macos-lg p-8 shadow-macos-lg hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">Email</h3>
                </div>
              </div>
              <div className="text-gray-300">
                <a
                  href="mailto:sales@rfdigi.co.za"
                  className="text-xl font-medium hover:text-primary-400 transition-colors"
                >
                  sales@rfdigi.co.za
                </a>
                <p className="text-sm text-gray-400 mt-2">
                  We'll respond within 24 hours
                </p>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-gray-800/30 backdrop-blur-macos border border-gray-700 rounded-macos-lg p-8 shadow-macos-lg">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-white mb-6">Business Hours</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                <div>
                  <h4 className="text-lg font-medium text-gray-300 mb-2">Weekdays</h4>
                  <p className="text-primary-400 font-semibold">Monday - Friday</p>
                  <p className="text-gray-400">8:00 AM - 5:00 PM (SAST)</p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-300 mb-2">Support</h4>
                  <p className="text-primary-400 font-semibold">24/7 Online</p>
                  <p className="text-gray-400">Email support available anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24">
        <div className="px-4 mx-auto text-center sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
              Ready to optimize your URLs?
            </h2>
            <p className="mb-10 text-lg text-gray-300">
              Join developers and IoT teams who trust our platform for their URL shortening needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!currentUser ? (
                <>
                  <Link
                    to="/register"
                    className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                  >
                    <span className="relative z-10">Start Building Today</span>
                    <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <a
                    href="mailto:sales@rfdigi.co.za"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-300 transition-all duration-300 border border-gray-600 rounded-macos-lg hover:text-white hover:border-gray-500 hover:bg-gray-800/50 backdrop-blur-sm"
                  >
                    Contact Sales
                  </a>
                </>
              ) : (
                <Link
                  to="/dashboard"
                  className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                >
                  <span className="relative z-10">Go to Dashboard</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;